import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PermissionGrantedActions } from './permission-granted.actions';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Action } from '@ngrx/store';
import {
  PermissionLevel,
  User_Permission,
  UserType,
  userTypeToJSON,
} from '@enrollhere/classes';
import { AuthService } from '../../../services/auth.service';
import { getUserCollection } from '../../../misc/getUserCollection';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class PermissionGrantedEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  allAgenciesLoading = false;
  userIsAdmin = false;
  allAgenciesPermissions: User_Permission[] | null = null;

  private toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  private emitPermissionsGrantedSuccess(
    subscriber: { next: (action: Action) => void },
    permissionsGranted: User_Permission[]
  ) {
    subscriber.next(
      PermissionGrantedActions.loadPermissionsGrantedSuccess({
        permissionsGranted,
      })
    );
  }

  private emitPermissionsGrantedFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    console.log('error', error);
    subscriber.next(
      PermissionGrantedActions.loadPermissionsGrantedFailure({
        error: this.toError(error),
      })
    );
  }

  private createAdminPermission(
    data: Record<string, unknown>,
    agencyId: string,
    userId: string,
    grantedUserType: UserType | undefined,
    isSadAdmin: boolean
  ): User_Permission {
    return {
      active: true,
      permissions: {},
      roles: isSadAdmin
        ? {
            admin: true,
            dialer: {
              admin: true,
            },
          }
        : {
            dialer: {
              viewer: true,
            },
          },
      user: {
        ...data,
        canImpersonate: true,
        id: agencyId,
        userType: UserType.AGENCY,
      },
      granterUserId: agencyId,
      granterUserType: UserType.AGENCY,
      grantedUserId: userId,
      grantedUserType,
    };
  }

  private buildAdminPermissions(
    agencies: Array<{ id: string; data: () => Record<string, unknown> }>,
    userId: string,
    grantedUserType: UserType | undefined,
    isSadAdmin: boolean
  ) {
    const permissionList: User_Permission[] = [];

    for (const agencyDoc of agencies) {
      const data = agencyDoc.data();
      if (data['name']) {
        permissionList.push(
          this.createAdminPermission(
            data,
            agencyDoc.id,
            userId,
            grantedUserType,
            isSadAdmin
          )
        );
      }
    }

    return permissionList;
  }

  private handleAdminAgencyPermissions(
    subscriber: { next: (action: Action) => void },
    agencies: Array<{ id: string; data: () => Record<string, unknown> }>,
    userId: string,
    grantedUserType: UserType | undefined,
    isSadAdmin: boolean
  ) {
    const permissionList = this.buildAdminPermissions(
      agencies,
      userId,
      grantedUserType,
      isSadAdmin
    );

    this.allAgenciesPermissions = permissionList;
    this.emitPermissionsGrantedSuccess(subscriber, permissionList);
  }

  private buildSnapshotPermissions(
    docs: Array<{ id: string; data: () => Record<string, unknown> }>,
    userId: string,
    grantedUserType: UserType | undefined
  ) {
    return docs.map(snapshotDoc => {
      const permission = User_Permission.fromJSON({
        ...snapshotDoc.data(),
        userId: snapshotDoc.id,
        grantedUserId: userId,
        grantedUserType,
      });

      if (
        this.authService.userInfo?.type &&
        this.authService.userInfo?.type !== UserType.AGENT &&
        permission.user
      ) {
        permission.user.canImpersonate = true;
      }

      return permission;
    });
  }

  private createAgencySelfPermission(
    userId: string,
    grantedUserType: UserType
  ): User_Permission {
    return {
      active: true,
      granterUserId: userId,
      granterUserType: UserType.AGENCY,
      grantedUserId: userId,
      grantedUserType,
      permissions: {
        all: PermissionLevel.PL_WRITE,
      },
      user: {
        canImpersonate: true,
        id: userId,
        userType: UserType.AGENCY,
      },
    };
  }

  loadPermissionsGranted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PermissionGrantedActions.loadPermissionsGranted),
      mergeMap(({ userId, userType, grantedUserType }) => {
        return new Observable<Action>(subscriber => {
          console.log('loadPermissionsGranted', {
            userId,
            userType,
            grantedUserType,
          });

          if (this.authService.userInfo?.claims['dialerSAD']) {
            if (!this.allAgenciesLoading) {
              this.allAgenciesLoading = true;
              this.userIsAdmin = true;
              getDocs(
                query(
                  collection(this.firestore, 'agencies'),
                  where('features.dialer', '==', true)
                )
              ).then(agencies =>
                this.handleAdminAgencyPermissions(
                  subscriber,
                  agencies.docs as Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>,
                  userId,
                  grantedUserType,
                  true
                )
              );
            } else if (this.allAgenciesPermissions) {
              this.emitPermissionsGrantedSuccess(
                subscriber,
                this.allAgenciesPermissions
              );
            }
          } else if (this.authService.userInfo?.claims['dialerSRA']) {
            if (!this.allAgenciesLoading) {
              this.allAgenciesLoading = true;
              this.userIsAdmin = true;
              getDocs(
                query(
                  collection(this.firestore, 'agencies'),
                  where('features.dialer', '==', true)
                )
              ).then(agencies =>
                this.handleAdminAgencyPermissions(
                  subscriber,
                  agencies.docs as Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>,
                  userId,
                  grantedUserType,
                  false
                )
              );
            } else if (this.allAgenciesPermissions) {
              this.emitPermissionsGrantedSuccess(
                subscriber,
                this.allAgenciesPermissions
              );
            }
          }

          let collectionName = 'users';
          if (grantedUserType) {
            const collectionNameString = getUserCollection(grantedUserType);
            if (collectionNameString) {
              collectionName = collectionNameString;
            }
          }

          let ref = query(
            collection(
              this.firestore,
              collectionName,
              userId,
              'private',
              'permissions',
              'received'
            )
          );

          if (userType) {
            ref = query(ref, where('userType', '==', userTypeToJSON(userType)));
          }

          const unsubscribe = onSnapshot(
            ref,
            snapshot => {
              const permissionList = this.buildSnapshotPermissions(
                snapshot.docs as Array<{
                  id: string;
                  data: () => Record<string, unknown>;
                }>,
                userId,
                grantedUserType
              );

              if (
                this.authService.userInfo?.user?.uid &&
                this.authService.userInfo.type === UserType.AGENCY
              ) {
                permissionList.push(
                  this.createAgencySelfPermission(
                    this.authService.userInfo.user.uid,
                    UserType.AGENCY
                  )
                );
              }

              if (!this.userIsAdmin) {
                this.emitPermissionsGrantedSuccess(subscriber, permissionList);
              }
            },
            error => {
              this.emitPermissionsGrantedFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          // return unsubscribe;
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({
              type: '[PermissionGranted API] Load permissionsGranted Error',
              error,
            })
          )
        );
      })
    );
  });
}
